import { useState } from 'react';
import { Button, Form, Input, Select, DatePicker, message } from 'antd';
import Container from '../../components/Layout/Container';
import common from './Common.module.scss';
import local from './Beginning.module.scss';
import startAvatar from '../../assets/images/start-avatar.svg';
import beginningAvatar from '../../assets/images/beginning-avatar.svg';
import beginning2Avatar from '../../assets/images/thinking-avatar.svg';
import { useNavigate } from 'react-router-dom';
import { useSurveyContext } from '../../context/SurveyContext';
import { COUNTRY_LIST } from '../../config';
import dayjs, { Dayjs } from 'dayjs';

const genderOptions = [
  { label: 'Female', value: 'female' },
  { label: 'Male', value: 'male' },
  { label: 'Non-binary', value: 'non-binary' },
  { label: 'Prefer not to say', value: 'prefer-not-to-say' },
];

const languageOptions = [
  { label: 'English', value: 'en' },
];

const countryOptions = COUNTRY_LIST.map(country => ({
  label: country,
  value: country,
}));

const Beginning = () => {
  const [currentStep, setCurrentStep] = useState<'start' | 'beginning' | 'beginning-2'>('start');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [ageError, setAgeError] = useState<string>('');
  const navigate = useNavigate();
  const { setDemographics, startSurvey } = useSurveyContext();

  // Function to calculate age from date of birth
  const calculateAge = (dob: Dayjs): number => {
    const today = dayjs();
    return today.diff(dob, 'year');
  };

  // Function to validate age
  const validateAge = (dob: Dayjs): boolean => {
    const age = calculateAge(dob);
    if (age < 18) {
      setAgeError(`You must be at least 18 years old to take this survey.`);
      return false;
    }
    setAgeError('');
    return true;
  };

  // Scroll to top when local currentStep changes
  // useEffect(() => {
  //   window.scrollTo({
  //     top: 120,
  //     left: 0,
  //     behavior: 'smooth'
  //   });
  // }, [currentStep]);

  const handleFinish = (values: any) => {
    setLoading(true);
    
    // Validate age before proceeding
    if (!validateAge(values.dob)) {
      setLoading(false);
      message.error('You must be at least 18 years old to take this survey.');
      return;
    }
    
    // Convert dayjs object to string for DOB
    const formattedValues = {
      ...values,
      dob: values.dob.format('YYYY-MM-DD'),
    };
    setDemographics(formattedValues);
    setLoading(false);
    setCurrentStep('beginning-2');
  };

  const handleStartSurvey = () => {
    startSurvey();
    navigate('/survey/start');
  };

  // Render different content based on current step
  const renderContent = () => {
    switch (currentStep) {
      case 'start':
        return (
          <>
            <div className={common.surveyAvatarWrapper}>
              <img src={startAvatar} alt="Survey Avatar" className={common.surveyAvatar} />
            </div>
            <div className={common.surveyTextSection}>
              <h1 className={common.surveyTitle}>
                Welcome to Your <br />Self- Discovery Journey
              </h1>
              <div className={common.surveySubtitle}>
                Unlock the path to your most authentic self
              </div>
              <div className={common.surveyParagraphs}>
                <p>This guided assessment is designed to help you gain deep self-awareness, uncover mental roadblocks, and align your strengths with meaningful career directions. Through a series of carefully structured questions, we’ll explore the inner dimensions that shape your mindset, identity, and future. Here’s what to expect:</p>
                <p style={{ marginBottom: 8 }}>Three Key Sections:</p>
                <ol style={{ paddingLeft: 20 }}>
                  <li>Understanding Your Inner Blocks: Explore your subconscious beliefs and uncover what’s holding you back.</li>
                  <li>Identifying Your Core Archetype: Discover your dominant personality traits and how they shape your choices in life.</li>
                  <li>Clarifying Your Career Direction: Match your passions, skills, and values with fulfilling life paths.</li>
                </ol>
                <p style={{ marginBottom: 8 }}>Format:</p>
                <ul style={{ paddingLeft: 20 }}>
                  <li>Approx. 10–15 minutes to complete</li>
                  <li>No right or wrong answers—this is a self-discovery tool</li>
                  <li>Be as honest as possible for the most accurate results. Answer as who you are not as who you aspire to be.</li>
                </ul>
              </div>
              <div className={common.surveyButtonWrapper}>
                <Button 
                  type="primary" 
                  className={common.surveyButton} 
                  onClick={() => setCurrentStep('beginning')} 
                  block
                >
                  Get Started
                </Button>
              </div>
            </div>
          </>
        );

      case 'beginning':
        return (
          <>
            <div className={common.surveyAvatarWrapper}>
              <img src={beginningAvatar} alt="Survey Avatar" className={common.surveyAvatar} />
            </div>
            <div className={common.surveyTextSection}>
              <h1 className={common.surveyTitle}>Ready to begin?</h1>
              <div className={common.surveyParagraphs}>
                <p>To personalize your results and increase the accuracy of your insights, we ask for your gender and date of birth.<br />These details allow us to:</p>
                <ul>
                  <li>Align your results with age-specific life transitions and career development phases</li>
                  <li>Provide development insights aligned with your cognitive and emotional growth</li>
                  <li>Reflect gender-based psychological patterns identified in behavioral and personality research</li>
                </ul>
                <p style={{ marginTop: 16, color: '#666', fontSize: '14px' }}>
                  <strong>Note:</strong> You must be at least 18 years old to take this survey.
                </p>
              </div>
              <div className={common.surveyFormWrapper}>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  requiredMark={false}
                  autoComplete="off"
                >
                  <Form.Item
                    name="gender"
                    label="Gender*"
                    rules={[{ required: true, message: 'Please select your gender' }]}
                  >
                    <Select placeholder="Select your gender" size="large" options={genderOptions} />
                  </Form.Item>
                  <Form.Item
                    name="dob"
                    label="Date of birth*"
                    rules={[{ required: true, message: 'Please select your date of birth' }]}
                    validateStatus={ageError ? 'error' : ''}
                    help={ageError}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      size="large" 
                      format="DD/MM/YYYY" 
                      placeholder="dd/mm/yyyy"
                      onChange={(date: Dayjs | null) => {
                        if (date) {
                          validateAge(date);
                        } else {
                          setAgeError('');
                        }
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="language"
                    label="Language*"
                    rules={[{ required: true, message: 'Please select your language preference' }]}
                  >
                    <Select placeholder="Select your language preference" size="large" options={languageOptions} />
                  </Form.Item>
                  <Form.Item
                    name="country"
                    label="Country*"
                    rules={[{ required: true, message: 'Please select your country' }]}
                  >
                    <Select 
                      placeholder="Select your country" 
                      size="large" 
                      options={countryOptions}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="email"
                    label="Email* (we'll send your report here)"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' },
                    ]}
                  >
                    <Input size="large" placeholder="Enter your email" />
                  </Form.Item>
                </Form>
              </div>
              <div className={common.surveyButtonWrapper}>
                <Button
                  type="primary"
                  className={common.surveyButton}
                  loading={loading}
                  onClick={() => form.submit()}
                  block
                >
                  Continue
                </Button>
              </div>
            </div>
          </>
        );

      case 'beginning-2':
        return (
          <>
            <div className={common.surveyAvatarWrapper}>
              <img src={beginning2Avatar} alt="Survey Avatar" className={common.surveyAvatar} />
            </div>
            <div className={common.surveyTextSection}>
              <h1 className={common.surveyTitle}>Section 1: Limiting Beliefs Check-in</h1>
              <div className={`${common.surveyParagraphs} ${local.centeredText}`}>
                <p>Some beliefs once protected you but may not serve you anymore. In this section, we’ll assess for your dominant limiting beliefs that make starting or finishing harder - like fear of rejection, not feeling enough, social isolation etc. If no dominant pattern show up, we’ll let you know and move on.</p>
                <p>
                This isn’t a test — it’s a mirror. Answer as who you are. Not as who you want to be. <br />
                The more honest you are, the clearer your personal insights will be.
                </p>
                <div>
                  <span>
                    <b>🕒 Estimated time:</b> around 10 minutes
                  </span>
                </div>
              </div>
              <div className={common.surveyButtonWrapper}>
                <Button 
                  type="primary" 
                  className={common.surveyButton} 
                  onClick={handleStartSurvey} 
                  block
                >
                  Continue
                </Button>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`${common.surveyWrapper} ${local.surveyWrapper}`}>
      <Container>
        {renderContent()}
      </Container>
    </div>
  );
};

export default Beginning; 